import { PropertyDefinitions } from 'hs-resources/resources/PropertyDefinition';
import { useResource, useResources, watch } from 'pulse/react';
import { useMemo } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import { useHttpClient } from '../../client/HttpClientContext';
import { keyBy } from '../../utils/keyBy';
import { usePropertyDefinitionOverride } from '../context/PropertyDefinitionOverrideContext';
import { useObjectTypeId } from './useObjectTypeId';
import { useProperty } from './useProperty';
import { getOwnProperty } from '../../utils/getOwnProperty';
const defaultResourceData = [];
const keyByName = memoizeOne(properties => keyBy(properties, ({
  property: {
    name
  }
}) => name));
export const usePropertyDefinitionByName = (objectTypeId, options = {
  skip: false
}) => {
  const propertyDefinitionOverride = usePropertyDefinitionOverride();
  const httpClient = useHttpClient();
  const {
    data: resourceData = defaultResourceData,
    loading,
    error: resourceError
  } = useResource(PropertyDefinitions, {
    httpClient,
    skip: options.skip,
    params: {
      objectTypeId,
      showHighlySensitiveProperties: true
    }
  });
  let propertyDefinitionsByName = useMemo(() => keyByName(resourceData), [resourceData]);
  const error = resourceError || undefined;
  if (propertyDefinitionOverride) {
    // We need to copy here because we cannot mutate pulse resources.
    propertyDefinitionsByName = Object.assign({}, propertyDefinitionsByName, {
      [propertyDefinitionOverride.property.name]: propertyDefinitionOverride
    });
  }
  return {
    propertyDefinitionsByName,
    error,
    loading
  };
};

/**
 * Fetches the property definition for the current property or a specific property by name.
 */
export const usePropertyDefinition = ({
  skip,
  propertyName
} = {}) => {
  const property = useProperty();
  const objectTypeId = useObjectTypeId();
  const {
    propertyDefinitionsByName = {},
    error,
    loading
  } = usePropertyDefinitionByName(objectTypeId, {
    skip
  });
  const targetPropertyName = propertyName || property.name;
  const propertyDefinition = getOwnProperty(propertyDefinitionsByName, targetPropertyName);
  return {
    propertyDefinition,
    error,
    loading
  };
};
export const usePropertyDefinitionsByObjectTypeId = (objectTypeIds, options = {
  skip: false
}) => {
  const httpClient = useHttpClient();
  const objectTypeIdsString = useMemo(() => objectTypeIds.join(','), [objectTypeIds]);
  const memoizedObjectIds = useMemo(() => objectTypeIds.map(objectTypeId => watch(PropertyDefinitions, {
    httpClient,
    skip: options.skip,
    params: {
      objectTypeId,
      showHighlySensitiveProperties: true
    }
  })),
  // objectTypeIds is joined to a stable key so inline-array callers don't recompute every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [httpClient, objectTypeIdsString, options.skip]);
  const results = useResources(memoizedObjectIds);
  return useMemo(() => {
    var _results$find;
    if (options.skip) {
      return {
        loading: false,
        data: undefined,
        error: undefined
      };
    }
    if (results.some(result => result.loading)) {
      return {
        loading: true,
        data: undefined,
        error: undefined
      };
    }
    const error = (_results$find = results.find(result => result.error)) === null || _results$find === void 0 ? void 0 : _results$find.error;
    if (error) {
      return {
        loading: false,
        data: undefined,
        error
      };
    }
    const data = objectTypeIds.reduce((acc, objectTypeId, index) => {
      var _results$index$data;
      acc[objectTypeId] = keyByName((_results$index$data = results[index].data) !== null && _results$index$data !== void 0 ? _results$index$data : defaultResourceData);
      return acc;
    }, {});
    return {
      loading: false,
      data,
      error: undefined
    };
    // objectTypeIds is joined to a stable key so inline-array callers don't recompute every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectTypeIdsString, options.skip, results]);
};